<template>
  <div class="aprovacao-doacoes-page">
    <nav class="page-breadcrumb mb-2">
      <router-link v-if="$can('read', 'PedidosVenda')" :to="{ name: 'pedidos-vendas' }" class="breadcrumb-link">
        <feather-icon icon="ArrowLeftIcon" size="14" />
        <span>Pedidos de Vendas</span>
      </router-link>
      <feather-icon v-if="$can('read', 'PedidosVenda')" icon="ChevronRightIcon" size="14" class="breadcrumb-separator" />
      <span class="breadcrumb-current">Aprovações de Doação (WD)</span>
    </nav>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">
          <feather-icon icon="CheckCircleIcon" size="20" class="mr-50 text-warning" />
          Doações aguardando aprovação
        </h4>
        <b-button variant="outline-primary" size="sm" :disabled="isLoading" @click="carregarPendentes">
          <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
          Atualizar
        </b-button>
      </div>

      <b-overlay :show="isLoading" rounded="sm">
        <b-alert :show="!!erroLista" variant="danger" class="p-2">{{ erroLista }}</b-alert>
        <b-alert :show="!isLoading && !erroLista && pendentes.length === 0" variant="secondary" class="p-2">
          Nenhum pedido de doação aguardando aprovação.
        </b-alert>

        <b-table
          v-if="pendentes.length > 0"
          :items="pendentes"
          :fields="fields"
          striped
          bordered
          hover
          responsive
        >
          <template #cell(pedidoid)="row">
            <strong>{{ row.item.pedidoid }}</strong>
            <small class="d-block">Sua aprovação: {{ (row.item.categoriasAprovacao || []).join(' / ') }}</small>
            <small v-if="row.item.outrasCategorias && row.item.outrasCategorias.length" class="d-block text-warning">
              Também contém {{ row.item.outrasCategorias.join(' / ') }} — aprovação de outro supervisor.
            </small>
          </template>
          <template #cell(pedidodata)="row">
            {{ formatarData(row.item.pedidodata) }}
          </template>
          <template #cell(valor)="row">
            {{ valorPedido(row.item) | formtPriceBr }}
          </template>
          <template #cell(acoes)="row">
            <div class="approval-actions">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-1"
              @click="verDetalhes(row.item)"
            >
              <feather-icon icon="EyeIcon" size="14" class="mr-25" />
              Detalhes
            </b-button>
            <b-button
              size="sm"
              variant="outline-info"
              class="mr-1"
              @click="abrirLogs(row.item)"
            >
              <feather-icon icon="ListIcon" size="14" class="mr-25" />
              Logs
            </b-button>
            <b-button
              size="sm"
              variant="success"
              :disabled="processando || row.item.aprovadoPeloSupervisor"
              @click="confirmarAprovacao(row.item)"
            >
              <b-spinner v-if="aprovandoId === row.item.pedidoid" small class="mr-25" />
              <feather-icon v-else icon="CheckIcon" size="14" class="mr-25" />
              {{ row.item.aprovadoPeloSupervisor ? 'Aprovado — aguardando outro supervisor' : 'Aprovar' }}
            </b-button>
            <b-button size="sm" variant="outline-danger" :disabled="processando" @click="abrirRejeicao(row.item)">
              <b-spinner v-if="rejeitandoId === row.item.pedidoid" small class="mr-25" />
              <feather-icon v-else icon="XIcon" size="14" class="mr-25" />
              Rejeitar
            </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </b-card>

    <b-modal
      v-model="showRejeicaoModal"
      :title="`Rejeitar doação — Pedido ${rejeicaoPedido ? rejeicaoPedido.pedidoid : ''}`"
      ok-title="Rejeitar e estornar"
      ok-variant="danger"
      cancel-title="Voltar"
      :ok-disabled="processando || !motivoRejeicao.trim()"
      :busy="processando"
      :no-close-on-backdrop="processando"
      :no-close-on-esc="processando"
      :hide-header-close="processando"
      @ok.prevent="rejeitar"
    >
      <p>O pedido será rejeitado e o valor debitado será devolvido ao <strong>budget de origem</strong>.
        A doação não será enviada ao Datasul.</p>
      <b-form-group label="Motivo da rejeição" label-for="motivo-rejeicao">
        <b-form-textarea id="motivo-rejeicao" v-model="motivoRejeicao" :disabled="processando"
          rows="3" maxlength="1000" required placeholder="Informe por que esta doação foi rejeitada." />
      </b-form-group>
      <b-alert :show="!!erroRejeicao" variant="danger" role="alert">{{ erroRejeicao }}</b-alert>
      <p v-if="rejeitandoId !== null" class="mb-0" role="status">
        <b-spinner small class="mr-50" /> Rejeitando e conferindo o estorno…
      </p>
    </b-modal>

    <b-modal v-model="showDetalhesModal" :title="`Itens para sua aprovação — Pedido ${detalhesPedidoId}`" size="xl" ok-only ok-title="Fechar">
      <b-overlay :show="isLoadingDetalhes" rounded="sm">
        <b-alert :show="!!erroDetalhes" variant="danger">{{ erroDetalhes }}</b-alert>
        <template v-if="detalhesPedido">
          <p>Categoria da sua aprovação: <strong>{{ (detalhesPedido.categoriasAprovacao || []).join(' / ') }}</strong>.</p>
          <b-alert :show="!!(detalhesPedido.outrasCategorias && detalhesPedido.outrasCategorias.length)" variant="warning">
            Este mesmo pedido também contém itens {{ (detalhesPedido.outrasCategorias || []).join(' / ') }}.
            Eles não estão nesta aprovação. O pedido completo só segue ao Datasul após os dois aceites.
          </b-alert>
          <b-table :items="detalhesPedido.itens || []" :fields="camposDetalhes" responsive striped>
            <template #cell(valorUnitario)="row">{{ row.item.valorUnitario | formtPriceBr }}</template>
            <template #cell(valorTotal)="row">{{ row.item.valorTotal | formtPriceBr }}</template>
          </b-table>
          <p class="text-right">Valor dos itens exibidos: <strong>{{ detalhesPedido.pedidovalortotal | formtPriceBr }}</strong></p>
        </template>
      </b-overlay>
    </b-modal>

    <!-- Modal de logs de auditoria -->
    <b-modal
      v-model="showLogsModal"
      :title="`Logs de aprovação — Pedido ${logsPedidoId}`"
      size="lg"
      ok-only
      ok-title="Fechar"
    >
      <b-overlay :show="isLoadingLogs" rounded="sm">
        <b-alert :show="!isLoadingLogs && logs.length === 0" variant="secondary" class="p-2">
          Nenhum log registrado para este pedido.
        </b-alert>
        <div v-for="log in logs" :key="log.aprovacaologid" class="log-entry mb-1 p-1 border rounded">
          <div class="d-flex justify-content-between">
            <b-badge :variant="badgeVariant(log.acao)">{{ labelAcao(log.acao) }}</b-badge>
            <small class="text-muted">{{ formatarDataHora(log.created_at) }}</small>
          </div>
          <div class="mt-50">
            <small>
              <strong>Usuário:</strong> {{ log.usuario_nome || log.usuario_id || '—' }}
              <span v-if="log.perfil_nome"> · <strong>Perfil:</strong> {{ log.perfil_nome }}</span>
            </small>
          </div>
          <div v-if="log.detalhes" class="mt-50">
            <small v-if="log.detalhes.mensagem" class="d-block">{{ log.detalhes.mensagem }}</small>
            <small v-if="log.detalhes.motivo" class="d-block">Motivo: {{ log.detalhes.motivo }}</small>
            <small v-if="log.detalhes.pedidonumerointegracao" class="d-block">
              Nº integração: {{ log.detalhes.pedidonumerointegracao }}
            </small>
            <div v-if="log.detalhes.mudancas && log.detalhes.mudancas.length" class="mt-25">
              <small class="d-block font-weight-bold">Alterações de quantidade:</small>
              <small v-for="(m, idx) in log.detalhes.mudancas" :key="idx" class="d-block">
                • Item {{ m.itemId }}: {{ m.quantidadeAntes === null ? '—' : m.quantidadeAntes }} → {{ m.quantidadeDepois }}
                <span v-if="m.removido" class="text-danger">(removido)</span>
              </small>
            </div>
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BTable, BButton, BOverlay, BAlert, BModal, BBadge, BSpinner, BFormGroup, BFormTextarea,
} from 'bootstrap-vue';
import SlsPedidosClient from '@/api/Operacoes/sls-pedidos.client';
import BudgetClient from '@/api/Budget/budget.client';
import Notifier from '@/libs/Notifier';
import { formtPriceBr } from '@core/utils/filter';

const client = new SlsPedidosClient();
const budgetClient = new BudgetClient();
const notify = new Notifier();

export default {
  name: 'AprovacaoDoacoes',
  components: {
    BCard, BTable, BButton, BOverlay, BAlert, BModal, BBadge, BSpinner, BFormGroup, BFormTextarea,
  },
  filters: {
    formtPriceBr,
  },
  data() {
    return {
      isLoading: false,
      erroLista: '',
      pendentes: [],
      aprovandoId: null,
      showRejeicaoModal: false,
      rejeicaoPedido: null,
      motivoRejeicao: '',
      erroRejeicao: '',
      rejeitandoId: null,
      showLogsModal: false,
      isLoadingLogs: false,
      logs: [],
      logsPedidoId: null,
      showDetalhesModal: false,
      isLoadingDetalhes: false,
      detalhesPedidoId: null,
      detalhesPedido: null,
      erroDetalhes: '',
      camposDetalhes: [
        { key: 'codigo', label: 'ISBN' }, { key: 'descricao', label: 'Descrição' },
        { key: 'quantidade', label: 'Quantidade' }, { key: 'valorUnitario', label: 'Valor unitário' },
        { key: 'valorTotal', label: 'Valor total' },
      ],
      fields: [
        { key: 'pedidoid', label: 'Pedido', sortable: true },
        { key: 'pedidodata', label: 'Data', sortable: true },
        { key: 'cliente_nome', label: 'Cliente', sortable: true },
        { key: 'representante_nome', label: 'Representante', sortable: true },
        { key: 'usuario_nome', label: 'Criado por', sortable: true },
        { key: 'total_itens', label: 'Itens', sortable: true },
        { key: 'valor', label: 'Valor Total', sortable: true },
        { key: 'acoes', label: 'Ações' },
      ],
    };
  },
  computed: {
    processando() { return this.aprovandoId !== null || this.rejeitandoId !== null; },
    isSupervisorPerfil() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        return !!perfilLogado
          && String(perfilLogado.perfil_acesso_nome || '').trim().toLowerCase() === 'supervisor';
      } catch (e) {
        return false;
      }
    },
    isAdminPerfil() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        const nome = String((perfilLogado && perfilLogado.perfil_acesso_nome) || '').trim();
        return ['Administrador', 'AdminDKP', 'AdminHanes'].includes(nome);
      } catch (e) {
        return false;
      }
    },
  },
  async mounted() {
    // Supervisor e Admins entram direto; qualquer outro perfil só entra se for
    // um APROVADOR ALTERNATIVO (delegação ativa de um supervisor — férias/ausência).
    if (!this.isSupervisorPerfil && !this.isAdminPerfil) {
      let souDelegado = false;
      try {
        const { data: res } = await budgetClient.delegacaoStatus();
        souDelegado = !!(res && res.data && res.data.podeAcessarAprovacoesComoDelegado);
      } catch (e) {
        souDelegado = false;
      }
      if (!souDelegado) {
        notify.error('Acesso restrito ao perfil Supervisor (ou aprovador delegado).');
        this.$router.push({ name: 'pedidos-vendas' });
        return;
      }
    }
    await this.carregarPendentes();
  },
  methods: {
    valorPedido(pedido) {
      const total = parseFloat(pedido.pedidototalcomimpostos || 0)
        || parseFloat(pedido.pedidovalortotal || 0) || 0;
      return total;
    },
    formatarData(data) {
      if (!data) return '—';
      const d = new Date(data);
      if (Number.isNaN(d.getTime())) return String(data);
      return d.toLocaleDateString('pt-BR');
    },
    formatarDataHora(data) {
      if (!data) return '—';
      const d = new Date(data);
      if (Number.isNaN(d.getTime())) return String(data);
      return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR')}`;
    },
    labelAcao(acao) {
      const map = {
        CRIACAO: 'Criação',
        ALTERACAO_QTD: 'Alteração de quantidade',
        APROVACAO: 'Aprovação',
        REJEICAO: 'Rejeição',
      };
      return map[acao] || acao;
    },
    badgeVariant(acao) {
      const map = {
        CRIACAO: 'light-primary',
        ALTERACAO_QTD: 'light-warning',
        APROVACAO: 'light-success',
        REJEICAO: 'light-danger',
      };
      return map[acao] || 'light-secondary';
    },
    async carregarPendentes() {
      this.isLoading = true;
      this.erroLista = '';
      try {
        const data = await client.getAprovacoesPendentes();
        this.pendentes = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erroLista = error.message || 'Não foi possível atualizar as doações pendentes. Tente novamente.';
      } finally {
        this.isLoading = false;
      }
    },
    async verDetalhes(pedido) {
      this.detalhesPedidoId = pedido.pedidoid;
      this.detalhesPedido = null;
      this.erroDetalhes = '';
      this.showDetalhesModal = true;
      this.isLoadingDetalhes = true;
      try {
        const resposta = await client.getDetalhesAprovacao(pedido.pedidoid);
        this.detalhesPedido = resposta.data || resposta;
      } catch (e) {
        this.erroDetalhes = e.message || 'Não foi possível carregar os itens para aprovação.';
      } finally {
        this.isLoadingDetalhes = false;
      }
    },
    abrirRejeicao(pedido) {
      if (this.processando) return;
      this.rejeicaoPedido = pedido;
      this.motivoRejeicao = '';
      this.erroRejeicao = '';
      this.showRejeicaoModal = true;
    },
    async rejeitar() {
      if (this.processando || !this.rejeicaoPedido) return;
      const motivo = this.motivoRejeicao.trim();
      if (!motivo || motivo.length > 1000) {
        this.erroRejeicao = 'Informe um motivo de até 1.000 caracteres.';
        return;
      }
      this.rejeitandoId = this.rejeicaoPedido.pedidoid;
      this.erroRejeicao = '';
      try {
        const resultado = await client.rejeitarPedidoDoacao(this.rejeitandoId, motivo);
        if (!resultado || resultado.success !== true) throw new Error('Não foi possível confirmar a rejeição. Atualize a lista antes de tentar novamente.');
        notify.success(resultado.message || 'Pedido rejeitado. Estorno conferido no budget de origem.');
        this.showRejeicaoModal = false;
        await this.carregarPendentes();
      } catch (error) {
        this.erroRejeicao = error.message || 'Não foi possível confirmar a rejeição. Consulte a lista e os logs antes de repetir.';
      } finally {
        this.rejeitandoId = null;
      }
    },
    async abrirLogs(pedido) {
      this.logsPedidoId = pedido.pedidoid;
      this.showLogsModal = true;
      this.isLoadingLogs = true;
      this.logs = [];
      try {
        const data = await client.getLogsAprovacao(pedido.pedidoid);
        this.logs = Array.isArray(data) ? data : [];
      } catch (error) {
        notify.error(error.message || 'Erro ao buscar logs.');
      } finally {
        this.isLoadingLogs = false;
      }
    },
    async confirmarAprovacao(pedido) {
      if (this.processando || pedido.aprovadoPeloSupervisor) return;
      const ok = await this.$bvModal.msgBoxConfirm(
        `Aprovar somente os itens ${(pedido.categoriasAprovacao || []).join(' / ')} do pedido ${pedido.pedidoid}? Se houver outra categoria, o pedido único aguardará também a aprovação do outro supervisor antes de seguir ao Datasul.`,
        {
          title: 'Confirmar aprovação',
          okTitle: 'Aprovar',
          okVariant: 'success',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      );
      if (!ok || this.processando) return;

      this.aprovandoId = pedido.pedidoid;
      try {
        const resultado = await client.aprovarPedidoDoacao(pedido.pedidoid);
        if (resultado && resultado.pendenteSegundaAprovacao) {
          this.$set(pedido, 'aprovadoPeloSupervisor', true);
        }
        notify.success((resultado && resultado.message) || 'Pedido aprovado com sucesso.');
        await this.carregarPendentes();
      } catch (error) {
        notify.error(error.message || 'Erro ao aprovar o pedido.');
      } finally {
        this.aprovandoId = null;
      }
    },
  },
};
</script>

<style scoped>
.page-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
}
.breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.log-entry {
  background: rgba(0, 0, 0, 0.02);
}
.approval-actions { display: flex; flex-wrap: wrap; gap: 0.4rem; min-width: 16rem; }
.approval-actions > .btn { margin-right: 0 !important; white-space: nowrap; }
</style>
